import { Alert } from '@/ui'
import { PendingDots, StatusRow } from '../StatusRow'
import { useMerchantSorting } from '../../hooks/useMerchantSorting'
import { SeparationGroupCard } from './SeparationCard'
import { toGroupKeys, UNSURE_VALUE, type SeparationRowPick } from './separation'
import type { WidgetProps } from './types'

/** The sorting card: it walks every group itself and tells Haven once they are all answered. */
export const SeparateTransactionsWidget = ({
  input,
  resolved,
  onOutcome,
}: WidgetProps) => {
  const {
    group,
    progress,
    loadFailed,
    retry,
    answer,
    saving,
    saveFailed,
    reportRefused,
    resendReport,
  } = useMerchantSorting(toGroupKeys(input), onOutcome, resolved)

  if (resolved) return null
  if (reportRefused) {
    return (
      <Alert
        variant="error"
        description="Your answers are saved, but Haven didn't get the summary."
        ctaText="Try again"
        ctaOnClick={resendReport}
      />
    )
  }
  if (loadFailed) {
    return (
      <Alert
        variant="error"
        description="Heard couldn't load these transactions right now."
        ctaText="Try again"
        ctaOnClick={retry}
      />
    )
  }
  if (!group) {
    return (
      <StatusRow
        label="Pulling in your transactions"
        pending
        trailing={<PendingDots />}
      />
    )
  }

  return (
    <SeparationGroupCard
      key={group.groupKey}
      group={group}
      progress={progress}
      disabled={resolved}
      saving={saving}
      saveFailed={saveFailed}
      onAnswer={(value) => void answer({ answer: value })}
      onHandOff={(note) => void answer({ answer: UNSURE_VALUE, note })}
      onSaveSplit={(picks: SeparationRowPick[]) =>
        void answer({
          rows: picks.map(({ transactionIds, value }) => ({
            transactionIds,
            answer: value,
          })),
        })
      }
    />
  )
}
